import { useMemo } from 'react';
import { useQuery } from 'react-query';

import useAuthority from '@app/hooks/useAuthority';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentMemberGroupList, useCurrentUserMemberId } from '@app/hooks/workspace';
import getAllList from '@app/libs/getAllList';
import { getNetPermission } from '@app/libs/permission/getNetPermission';
import { getEditingDocumentRoles } from '@app/store/transport-layer/document/editing-documents/roles';

type EditingDocumentPermission = {
  isLoading: boolean;
  hasEditingDocumentAuthority: boolean;
  readAllEditingDocumentPermission: { isPermitted: boolean; message: string };
  manageEditingDocumentPermission: { isPermitted: boolean; message: string };
};
type EditingDocumentDetailPermission = EditingDocumentPermission & {
  readablePermission: { isPermitted: boolean; message: string };
  editablePermission: { isPermitted: boolean; message: string };
};

export function useEditingDocumentPermission(): EditingDocumentPermission;
export function useEditingDocumentPermission({
  editingDocumentId,
}: {
  editingDocumentId: string;
}): EditingDocumentDetailPermission;
export function useEditingDocumentPermission({
  editingDocumentId,
}: { editingDocumentId?: string } = {}) {
  // 요금제 권한 판단
  const authority = useAuthority();
  const hasEditingDocumentAuthority = authority.data?.permissions.EDITING_DOCUMENT === true;

  // 워크스페이스 역할 판단
  const { permissionMap, isLoading } = useMemberPermissions();
  const readAllEditingDocumentPermission = getNetPermission({
    hasAuthorityPermission: hasEditingDocumentAuthority,
    hasRolePermission: permissionMap.READ_ALL_EDITING_DOCUMENT,
  });
  const currentUserMemberId = useCurrentUserMemberId();
  const manageEditingDocumentPermission = getNetPermission({
    hasAuthorityPermission: hasEditingDocumentAuthority,
    hasRolePermission: permissionMap.MANAGE_EDITING_DOCUMENT,
  });

  // 리소스 역할 판단
  const workspaceId = useWorkspaceId();
  const currentMemberGroups = useCurrentMemberGroupList();
  const editingDocumentRoles = useQuery({
    queryKey: [
      {
        path: ['workspaces', workspaceId, 'editing-documents', editingDocumentId, 'roles'],
        type: 'query',
        service: 'document',
        input: {
          workspaceId,
          editingDocumentId: editingDocumentId ?? '',
          params: {
            subjects: [
              `MEMBER/${currentUserMemberId}` as const,
              ...currentMemberGroups.map<`GROUP/${string}`>((g) => `GROUP/${g.id}` as const),
            ],
          },
        },
      },
    ],
    queryFn: ({ queryKey: [{ input }] }) =>
      getAllList({
        queryFn: async (offset, limit) => {
          const { count, editingDocumentRoles } = await getEditingDocumentRoles(
            input.workspaceId,
            input.editingDocumentId,
            {
              offset,
              limit,
              subjects: input.params.subjects,
            }
          );
          return { count, list: editingDocumentRoles };
        },
      }),
    enabled: !!workspaceId && !!editingDocumentId && !!currentUserMemberId,
    staleTime: 5 * 1000,
  });
  const hasViewerRole = useMemo(
    () => editingDocumentRoles.data?.some((role) => role.name === 'VIEWER') ?? false,
    [editingDocumentRoles.data]
  );
  const hasEditorRole = useMemo(
    () => editingDocumentRoles.data?.some((role) => role.name === 'EDITOR') ?? false,
    [editingDocumentRoles.data]
  );
  const readablePermission = getNetPermission({
    hasAuthorityPermission: hasEditingDocumentAuthority,
    hasRolePermission: readAllEditingDocumentPermission.isPermitted || hasViewerRole,
  });
  const editablePermission = getNetPermission({
    hasAuthorityPermission: hasEditingDocumentAuthority,
    hasRolePermission: manageEditingDocumentPermission.isPermitted || hasEditorRole,
  });

  if (editingDocumentId) {
    return {
      isLoading: isLoading || authority.isLoading || editingDocumentRoles.isLoading,
      hasEditingDocumentAuthority,
      readAllEditingDocumentPermission,
      manageEditingDocumentPermission,
      readablePermission,
      editablePermission,
    } as EditingDocumentDetailPermission;
  }

  return {
    isLoading: isLoading || authority.isLoading,
    hasEditingDocumentAuthority,
    readAllEditingDocumentPermission,
    manageEditingDocumentPermission,
  };
}
